import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import useElysiaStore from '@/app/elysia-store';
import notify from '../services/notify';
import {
  ALLOWED_TYPES,
  FILE_UPLOAD_SIZE_ID,
  FILE_UPLOAD_TYPE_ID,
  MAX_FILE_COUNT,
} from '../config/settings';
import { checkForDuplicateFiles } from '@/utils/validate';

export const useDragAndDropFileUpload = () => {
  const setAttachedFileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const setIsFileDropped = useElysiaStore((state) => state.setIsFileDropped);
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setDragAndDropFiles = useElysiaStore.getState().setDragAndDropFiles;

  const [isDragging, setIsDragging] = useState(false);
  const isDragActiveRef = useRef(false);

  const onDrop = (acceptedFiles: File[]) => {
    if (document.querySelector('.modal-content')) return;

    if (!useElysiaStore.getState().promptToolsConfig.attachFiles) {
      notify({
        message: 'This app does not support file uploads or adding sources.',
        type: 'error',
      });
      setIsDragging(false);
      return;
    }

    if (acceptedFiles.length > MAX_FILE_COUNT) {
      notify({
        message: `You can only drop up to ${MAX_FILE_COUNT} files at a time.`,
        type: 'error',
      });
      setIsDragging(false);
      return;
    }

    // Strip out duplicates (each gets its own toast); keep going with the rest
    const duplicates = checkForDuplicateFiles(acceptedFiles);
    const nonDuplicateFiles = acceptedFiles.filter(
      (file) => !duplicates.some((d) => d.name === file.name)
    );

    if (nonDuplicateFiles.length === 0) {
      setIsDragging(false);
      return;
    }

    const allowedTypes = [...ALLOWED_TYPES];

    const validFiles: File[] = [];
    const invalidType: string[] = [];
    const oversized: string[] = [];

    nonDuplicateFiles.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        invalidType.push(file.name);
        return;
      }
      if (file.size > 31457280) {
        oversized.push(file.name);
        return;
      }
      validFiles.push(file);
    });

    if (invalidType.length) {
      setBrandDoc(false);
      notify({
        message: `Elysia does not support this file type: ${invalidType.join(', ')}`,
        type: 'error',
        autoClose: false,
        toastId: FILE_UPLOAD_TYPE_ID,
      });
    }

    if (oversized.length) {
      notify({
        message: `File exceeds the max allowed size of 30 MB: ${oversized.join(', ')}`,
        type: 'error',
        autoClose: false,
        toastId: FILE_UPLOAD_SIZE_ID,
      });
    }

    if (validFiles.length > 0) {
      setAttachedFileArray((prev) => [...prev, ...validFiles]);
      setDragAndDropFiles(validFiles);
      setIsFileDropped(true);
    }

    setIsDragging(false);
  };

  const { getRootProps } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      if (document.querySelector('.modal-content')) return;
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      if (e.clientX === 0 && e.clientY === 0) {
        setIsDragging(false);
        setIsFileDropped(false);
      }
    };

    const handleDrop = () => {
      isDragActiveRef.current = false;
      setIsDragging(false);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
    // eslint-disable-next-line
  }, []);

  return { isDragging, getRootProps };
};
